// SPDX-License-Identifier: AGPL-3.0-or-later

import {SearchProviderPickerModal} from '@app/features/search/components/modals/SearchProviderPickerModal';
import ReverseImageSearch from '@app/features/search/state/ReverseImageSearch';
import {CopyLinkIcon, OpenMediaLinkIcon, SearchIcon} from '@app/features/ui/action_menu/ContextMenuIcons';
import {SearchProviderContextMenuItems} from '@app/features/ui/action_menu/items/SearchProviderMenuItems';
import {getSearchProviderMenuState} from '@app/features/ui/action_menu/items/SearchProviderMenuUtils';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

interface ReverseImageSearchMenuItemsProps {
	imageUrl: string | null | undefined;
	onClose?: () => void;
	wrapInGroup?: boolean;
	defaultLabel?: string;
	includeCopyAndOpen?: boolean;
	copyLabel?: string;
	openLabel?: string;
}
export const ReverseImageSearchMenuItems: React.FC<ReverseImageSearchMenuItemsProps> = observer(
	({imageUrl, onClose, wrapInGroup = false, defaultLabel, includeCopyAndOpen = false, copyLabel, openLabel}) => {
		const {t, i18n} = useLingui();
		const renderDefaultLabel = defaultLabel ?? t`Reverse Image Search`;
		const renderCopyLabel = copyLabel ?? t`Copy Image URL`;
		const renderOpenLabel = openLabel ?? t`Open Image in Browser`;
		const openWith = useCallback((engineId: string, url: string) => {
			const target = ReverseImageSearch.buildSearchUrl(engineId, url);
			if (target) {
				void openExternalUrl(target);
			}
		}, []);
		const handleSearch = useCallback(
			(engineId: string) => {
				if (!imageUrl) {
					onClose?.();
					return;
				}
				openWith(engineId, imageUrl);
				onClose?.();
			},
			[imageUrl, onClose, openWith],
		);
		const handleDefaultSearch = useCallback(() => {
			if (!imageUrl) {
				onClose?.();
				return;
			}
			const defaultEngine = ReverseImageSearch.defaultEngine;
			if (defaultEngine) {
				openWith(defaultEngine.id, imageUrl);
				onClose?.();
				return;
			}
			const targetImageUrl = imageUrl;
			onClose?.();
			ModalCommands.push(
				modal(() => (
					<SearchProviderPickerModal mode="image" onPick={(engineId) => openWith(engineId, targetImageUrl)} />
				)),
			);
		}, [imageUrl, onClose, openWith]);
		const handleCopy = useCallback(async () => {
			if (!imageUrl) {
				onClose?.();
				return;
			}
			await TextCopyCommands.copy(i18n, imageUrl, true);
			ToastCommands.createToast({
				type: 'success',
				children: i18n._(msg`Link copied to clipboard`),
			});
			onClose?.();
		}, [imageUrl, onClose, i18n]);
		const handleOpen = useCallback(() => {
			if (!imageUrl) {
				onClose?.();
				return;
			}
			void openExternalUrl(imageUrl);
			onClose?.();
		}, [imageUrl, onClose]);
		if (!imageUrl) {
			return null;
		}
		const state = getSearchProviderMenuState(ReverseImageSearch);
		const searchItems =
			state.enabledEngines.length > 0 ? (
				<SearchProviderContextMenuItems
					state={state}
					defaultLabel={renderDefaultLabel}
					renderIcon={() => <SearchIcon size={20} />}
					onDefaultSearch={handleDefaultSearch}
					onSearchWithEngine={(engine) => handleSearch(engine.id)}
				/>
			) : null;
		const copyAndOpen = includeCopyAndOpen ? (
			<>
				<MenuItem icon={<CopyLinkIcon size={20} />} onClick={handleCopy}>
					{renderCopyLabel}
				</MenuItem>
				<MenuItem icon={<OpenMediaLinkIcon size={20} />} onClick={handleOpen}>
					{renderOpenLabel}
				</MenuItem>
			</>
		) : null;
		if (!searchItems && !copyAndOpen) {
			return null;
		}
		const content = (
			<>
				{copyAndOpen}
				{searchItems}
			</>
		);
		if (wrapInGroup) {
			return <MenuGroup>{content}</MenuGroup>;
		}
		return content;
	},
);
